import './ScopedBlock.scss'
import React, { PropsWithChildren, ReactElement, ReactNode, useRef, useState } from 'react'
import { MissingScopeAction, ScopedBlockProps } from './models'
import { useAppSelector } from '../../store/hooks'
import { selectAuthenticatedUser } from '../../store/auth/authSlice'
import { useUserHasScopedBlockAccess } from './hooks'
import PrizeBadge from './PrizeBadge.svg?url'
import { Overlay, Tooltip } from 'react-bootstrap'
import { Button } from '../Button/Button'
import { useNavigate } from 'react-router-dom'
import { useOutsideClick } from '../../common/useOutsideClick'

export const ScopedBlock: React.FC<ScopedBlockProps> = ({
  scope,
  quota,
  authActions = [],
  missingScopeAction,
  children,
  tooltipElement,
  tooltipText,
  tooltipButtonText,
  upgradeContainerClassName = 'd-inline',
  showUpgradeBadge = true,
  modal,
  replaceElement,
}) => {
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const [showUpgradeTooltip, setShowUpgradeTooltip] = useState(false)
  const navigate = useNavigate()
  const { userHasScopedBlockAccess } = useUserHasScopedBlockAccess()
  const target = useRef(null)
  useOutsideClick(target, () => {
    setShowUpgradeTooltip(false)
  })

  if (!authenticatedUser) {
    return null
  }

  const [userHasAccess, accessCheckResult] = userHasScopedBlockAccess(authActions, quota, scope)

  if (missingScopeAction === MissingScopeAction.HIDE && !userHasAccess) {
    return <></>
  }

  if (missingScopeAction === MissingScopeAction.REPLACE && !userHasAccess) {
    return <>{replaceElement}</>
  }

  const applyPropsToChildren = (children: ReactNode, newProps = {}): ReactNode => {
    if (!children) {
      return
    }

    const childrenWithAppliedProps = React.Children.map(children, (child) => {
      if (!child || !(child as ReactElement).props) return child // is text node, without a tag
      const recursiveGrandchildren = applyPropsToChildren((child as ReactElement).props.children, newProps)
      const childNewProps = { children: recursiveGrandchildren, ...newProps }
      return React.cloneElement(child as ReactElement, childNewProps)
    })

    if (!childrenWithAppliedProps || Array.isArray(children)) {
      return childrenWithAppliedProps
    }
    return childrenWithAppliedProps[0]
  }

  const addUpgradeHandlerToChildren = () => {
    if (!children) {
      return
    }
    const upgradeBadge = showUpgradeBadge ? <img alt='Upgrade' src={PrizeBadge} /> : <></>

    const upgradeOverlay = (
      <Overlay target={target.current} show={showUpgradeTooltip} placement='top'>
        {(props) => {
          const customTooltip = tooltipElement ? React.cloneElement(tooltipElement as ReactElement, { accessCheckResult, ...props }) : null
          return (
            customTooltip ?? (
              <Tooltip id='overlay-example' className='upgrade-tooltip' {...props}>
                <div className='d-flex flex-column zocks-gap-1.25'>
                  <div className='text-nowrap'>
                    <img alt='Upgrade' src={PrizeBadge} />
                    {tooltipText ?? <b>Upgrade to unlock this feature</b>}
                  </div>
                  <Button
                    variant={'primary'}
                    className='py-1'
                    onClick={() => {
                      navigate('/account/plan')
                    }}
                  >
                    {tooltipButtonText ?? 'Get feature'}
                  </Button>
                </div>
              </Tooltip>
            )
          )
        }}
      </Overlay>
    )
    const handleUpgradeNeeded = (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation()
      e.preventDefault()
      setShowUpgradeTooltip(true)
    }

    const getReactElement = () => {
      const reactElement = children as ReactElement
      if (Array.isArray(reactElement)) {
        return reactElement[0]
      }
      return reactElement
    }

    const reactEl = getReactElement()
    if (reactEl.props && reactEl.props.children) {
      return {
        ...reactEl,
        props: {
          ...reactEl.props,
          ref: target,
          onClick: handleUpgradeNeeded,
          children: [...React.Children.toArray(reactEl.props.children), upgradeBadge, upgradeOverlay],
        },
      }
    }
    return React.createElement('div', { className: upgradeContainerClassName, ref: target, onClick: handleUpgradeNeeded }, [
      reactEl,
      upgradeBadge,
      upgradeOverlay,
    ])
  }

  if (missingScopeAction === MissingScopeAction.UPGRADE) {
    return <>{!userHasAccess ? addUpgradeHandlerToChildren() : children}</>
  }

  if (missingScopeAction === MissingScopeAction.MODAL) {
    return <>{!userHasAccess ? modal : children}</>
  }

  const disabled = missingScopeAction === MissingScopeAction.DISABLE && !userHasAccess
  return <>{disabled ? applyPropsToChildren(children, { disabled }) : children}</>
}

export const MultiScopedBlock: React.FC<{ scopedBlocks: ScopedBlockProps[] } & PropsWithChildren> = ({ scopedBlocks, children }) => {
  const { userHasScopedBlockAccess } = useUserHasScopedBlockAccess()
  const scopedBlockWithoutAccess = scopedBlocks.find((s) => {
    const [userHasAccess] = userHasScopedBlockAccess(s.authActions ?? [], s.quota, s.scope)
    return !userHasAccess
  })

  if (scopedBlockWithoutAccess) {
    return <ScopedBlock {...scopedBlockWithoutAccess}>{children}</ScopedBlock>
  }

  return <>{children}</>
}
